import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { GoPlus } from "react-icons/go";
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { ActionButtons } from '../../../Components/Form/ActionButtons/action-buttons.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { useListOrdersReplacement } from './replacement-order-list.hook';
import { ListOrdersReplacementUseCase } from '../../../Usecase/OrdersReplacement/list-orders-replacement-usecase';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { DestroyOrdersReplacementUseCase } from '../../../Usecase/OrdersReplacement/destroy-orders-replacement-usecase';
import { GetMaskProductOptionsUseCase } from '../../../Usecase/Products/get-mask-product-options-usecase';
import { Can } from '../../../Components/Can';
import { LinkClipBoard } from '../../../Components/LinkClipBoard/link-clip-board.component';
import { Flex } from '../../../Components/Flex/flex.component';
import { ListBrandFilter } from '../../../Components/List/list-brand-filter.component';

interface IOrdersReplacementList {
  listOrdersReplacementUseCase: ListOrdersReplacementUseCase
  destroyOrdersReplacementUseCase: DestroyOrdersReplacementUseCase
  getMaskProductOptionsUseCase: GetMaskProductOptionsUseCase
}

const storeChipColor = (store?: string) => {
  if (store === 'B2B') {
    return { bgcolor: '#e3f2fd', color: '#1565c0' };
  }
  if (store === 'B2C') {
    return { bgcolor: '#f3e5f5', color: '#6a1b9a' };
  }
  return { bgcolor: '#f5f5f5', color: '#616161' };
};

const OrdersReplacementList : FC<IOrdersReplacementList> = ({
  listOrdersReplacementUseCase,
  destroyOrdersReplacementUseCase,
  getMaskProductOptionsUseCase,
}) => {
    const navigate = useNavigate()

    const {
      list,
      setOrderToDestroy,
      orderToDestroy,
      orderToDestroyLabel,
      handleDestroyOrderReplacement,
      totalRegisters,
      loading,
      loadError,
      listOrdersReplacement,
      currentFilters,
      currentSearch,
      handleSearch,
      handlePageChange,
      handleBrandFilter,
      handleSortModelChange,
      sortModel,
      brandOptions,
    } = useListOrdersReplacement({
      listOrdersReplacementUseCase,
      destroyOrdersReplacementUseCase,
      getMaskProductOptionsUseCase,
    })

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'identifier',
            headerName: 'Pedido',
            width: 118,
            sortable: false,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={`/orders-replacement-form/${params.row.id}`}
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {params.value || '—'}
              </Box>
            ),
          },
          {
            field: 'productsSummaryLabel',
            headerName: 'Produtos',
            minWidth: 260,
            flex: 2,
            sortable: false,
            renderCell: (params: any) => (
              <Tooltip
                title={
                  <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                    {params.row.productsSummaryTooltip}
                  </Box>
                }
                placement="top-start"
                arrow
              >
                <Box sx={{ minWidth: 0, py: 0.5, lineHeight: 1.35, width: '100%' }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {params.row.productsSummaryLabel}
                  </Typography>
                  {params.row.productsSummaryDetail ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {params.row.productsSummaryDetail}
                    </Typography>
                  ) : null}
                </Box>
              </Tooltip>
            ),
          },
          {
            field: 'brand',
            headerName: 'Marca',
            minWidth: 150,
            flex: 1,
            sortable: false,
            renderCell: (params: any) => (
              <Typography
                color="text.secondary"
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                }}
              >
                {params.value || 'Não informada'}
              </Typography>
            ),
          },
          {
            field: 'store',
            headerName: 'Loja',
            width: 92,
            sortable: false,
            renderCell: (params: any) => (
              <Chip
                label={params.value || '—'}
                size="small"
                sx={{
                  fontWeight: 600,
                  ...storeChipColor(params.value),
                }}
              />
            ),
          },
          {
            field: 'createdAt',
            headerName: 'Criação',
            width: 118,
            sortable: true,
          },
          {
            field: 'dueDate',
            headerName: 'Entrega',
            width: 118,
            sortable: true,
          },
          {
            field: 'id',
            headerName: 'ID interno',
            minWidth: 180,
            flex: 1,
            sortable: false,
            renderCell: (params: any) => (
              <Flex height="100%" alignItems="center" sx={{ minWidth: 0, gap: 0.5 }}>
                <LinkClipBoard link={params.row.id} title="Copiar ID" />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {params.row.id}
                </Typography>
              </Flex>
            ),
          },
          {
            field: "actions",
            headerName: "Ações",
            width: 80,
            sortable: false,
            renderCell: (params: any) => (
              <ActionButtons
                params={params}
                setItemToDestroy={setOrderToDestroy}
                editLink={`/orders-replacement-form/${params.row.id}`}
              />
            ),
          },
        ];

        return createColumns;
      }, [setOrderToDestroy]);

    return (
      <Can permissions={['menu:replacementOrders:manage']}>
        <BaseScreen>
        <ConfirmDialog
          title='Remover Pedido de Reposição'
          onCancel={() => setOrderToDestroy('')}
          description={`Tem certeza que deseja remover o pedido "${orderToDestroyLabel}"?`}
          open={!!orderToDestroy}
          onConfirm={handleDestroyOrderReplacement}
        />
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage
                    title='Pedidos de Reposição'
                    subtitle='Gerencie pedidos de reposição de estoque e materiais'
                    breadcrumb={[
                      { title: 'Pedidos de Reposição', link: "/orders-replacement-list"},
                      { title: 'Listagem'},
                    ]}
                    minHeight="auto"
                    search
                    searchDefaultValue={currentSearch}
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch='Pesquisar por identificador ou marca'
                    toolbarActions={() => (
                      <ListBrandFilter
                        value={currentFilters.brand ?? ''}
                        options={brandOptions}
                        onChange={handleBrandFilter}
                        placeholder="Todas as marcas"
                      />
                    )}
                    Action={() => (
                      <Button
                        LeftIcon={() => <GoPlus size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 130 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => navigate('/orders-replacement-form')}
                      >
                        Adicionar
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        totalCount={totalRegisters}
                        columns={columns}
                        search={currentSearch}
                        page={currentFilters.page}
                        onPageChange={handlePageChange}
                        sortModel={sortModel}
                        sortingOrder={['asc', 'desc']}
                        onSortModelChange={handleSortModelChange}
                        sortingMode="server"
                        rowHeight={72}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={72} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar os pedidos de reposição."
                      onRetry={() => listOrdersReplacement({ ...currentFilters, page: currentFilters.page || 1 })}
                    />
                  ) : (
                    <Placeholder description="Nenhum pedido de reposição encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { OrdersReplacementList };
